import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatINR, formatCarats, formatGrams, formatDateTime } from '@/lib/format';
import { extractError } from '@/lib/errors';
import {
  ArrowLeft, RefreshCw, Building2, FileText, Wallet,
  ChevronDown, ChevronRight, ShieldAlert, ScrollText, Calendar, Download, ArrowDownToLine, Filter,
} from 'lucide-react';
import { MakePaymentDialog } from '@/components/phase10/MakePaymentDialog';
import { MakePurchaseInline } from '@/components/phase10/MakePurchaseInline';
// Wave 3.99k — Sticky section navigator + back-to-top pill on long pages.
import { StickySectionNav } from '@/components/StickySectionNav';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
// Wave 3.99n — Section tint helper.
import { sectionMat } from '@/lib/sectionTints';
import { PdfDownloadDialog } from '@/components/phase11/PdfDownloadDialog';
import PurchaseReturnDialog from '@/components/phase18/PurchaseReturnDialog';

// Indian Financial Year runs April \u2192 March. Compute current/last FY bounds.
function fyBounds(date = new Date(), offset = 0) {
  const y = date.getFullYear();
  const m = date.getMonth(); // 0 = Jan
  const fyStartYear = m >= 3 ? y : y - 1; // months 3..11 = Apr..Dec
  const start = new Date(Date.UTC(fyStartYear + offset, 3, 1, 0, 0, 0));
  const end = new Date(Date.UTC(fyStartYear + offset + 1, 2, 31, 23, 59, 59));
  return { from_date: start.toISOString(), to_date: end.toISOString() };
}

function monthBounds(date = new Date()) {
  const y = date.getFullYear();
  const m = date.getMonth();
  const start = new Date(Date.UTC(y, m, 1, 0, 0, 0));
  const end = new Date(Date.UTC(y, m + 1, 0, 23, 59, 59));
  return { from_date: start.toISOString(), to_date: end.toISOString() };
}

function quarterBounds(date = new Date()) {
  const y = date.getFullYear();
  const m = date.getMonth();
  const qStart = Math.floor(m / 3) * 3;
  const start = new Date(Date.UTC(y, qStart, 1, 0, 0, 0));
  const end = new Date(Date.UTC(y, qStart + 3, 0, 23, 59, 59));
  return { from_date: start.toISOString(), to_date: end.toISOString() };
}

const PERIOD_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'this_fy', label: 'This FY (Apr\u2013Mar)' },
  { value: 'last_fy', label: 'Last FY' },
  { value: 'custom', label: 'Custom range' },
];

function periodToDates(period, custom) {
  switch (period) {
    case 'this_month': return monthBounds();
    case 'this_quarter': return quarterBounds();
    case 'this_fy': return fyBounds(new Date(), 0);
    case 'last_fy': return fyBounds(new Date(), -1);
    case 'custom': {
      const fd = custom.from ? new Date(custom.from + 'T00:00:00Z').toISOString() : null;
      const td = custom.to ? new Date(custom.to + 'T23:59:59Z').toISOString() : null;
      return { from_date: fd, to_date: td };
    }
    default: return { from_date: null, to_date: null };
  }
}

const ENTRY_META = {
  purchase: { label: 'Purchase', tone: 'bg-blue-50 text-blue-700' },
  return: { label: 'Return', tone: 'bg-rose-50 text-rose-700' },
  payment: { label: 'Payment', tone: 'bg-emerald-50 text-emerald-700' },
  on_approval_in: { label: 'Approval In', tone: 'bg-amber-50 text-amber-800' },
  on_approval_return: { label: 'Approval Return', tone: 'bg-amber-50 text-amber-800' },
  convert_to_stock: { label: 'Converted to Stock', tone: 'bg-purple-50 text-purple-700' },
};

// Wave 3.36 — Entry-type filter groups (user-defined buckets to slice the
// ledger by activity flow). ``all`` shows everything. ``purchase_payment``
// focuses on the financial flow (purchase / convert-to-stock / payment /
// purchase return). ``approval`` focuses on the on-approval lifecycle.
// ``payment`` shows only payment rows.
const ENTRY_TYPE_GROUPS = {
  all: { label: 'All entries', types: null },
  purchase_payment: {
    label: 'Purchases & Payments',
    types: ['purchase', 'convert_to_stock', 'payment', 'return'],
  },
  approval: {
    label: 'Approval Movements',
    types: ['on_approval_in', 'on_approval_return', 'convert_to_stock'],
  },
  payment: { label: 'Payments Only', types: ['payment'] },
};

// IST date-only helper for due-date comparisons (UTC offset +05:30).
function todayIstIso() {
  return new Date(new Date().getTime() + (5.5 * 3600 * 1000))
    .toISOString()
    .slice(0, 10);
}

export default function VendorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <AppShell
      title="Vendor Detail"
      subtitle="Purchases, returns, payments, and on-approval stock for this vendor."
      right={
        <Button variant="outline" onClick={() => navigate(-1)} className="h-9" data-testid="vendor-back-button">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
      }
    >
      <CompanyGate>
        <VendorDetailContent id={id} />
      </CompanyGate>
      {/* Wave 3.99j — Global back-to-top pill. */}
      <ScrollToTopButton />
    </AppShell>
  );
}

function VendorDetailContent({ id }) {
  const { isSuperAdmin, hasPermission, effectiveCompanyId } = useAuth();
  // Wave 3.30 — Amount ledger gating: super admin OR users with the
  // ``vendors.amount_ledger.view`` permission can view the financial card.
  // Payment recording remains super_admin-only at the backend.
  const canViewAmountLedger = isSuperAdmin || hasPermission('vendors.amount_ledger.view');
  const [vendor, setVendor] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [error, setError] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  // Phase 12 \u2014 period filter state
  const [period, setPeriod] = useState('all');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [pdfDlg, setPdfDlg] = useState({ open: false });
  // Phase 18 — Purchase Return: open with the chosen material kind.
  const [prDlg, setPrDlg] = useState({ open: false, material: null });
  // Wave 3.36 — Entry-type filter (Purchases & Payments / Approval / Payment / All).
  const [entryTypeFilter, setEntryTypeFilter] = useState('all');
  // Wave 3.36 — Pending Bills dialog (single vendor).
  const [pendingDlgOpen, setPendingDlgOpen] = useState(false);
  const [pendingCutoff, setPendingCutoff] = useState('');
  const [pendingBusy, setPendingBusy] = useState(false);

  // Load vendor public info (everyone with company access can see)
  const loadVendor = async () => {
    try {
      const { data } = await api.get('/masters/vendors', { params: { company_id: effectiveCompanyId } });
      const v = (data || []).find((x) => x.id === id);
      if (!v) throw new Error('Vendor not found in your company');
      setVendor(v);
    } catch (e) { setError(e.message || 'Failed to load vendor'); }
  };

  // SA / users with vendors.amount_ledger.view: load amount ledger
  const loadLedger = async () => {
    if (!canViewAmountLedger) { setLedger(null); return; }
    try {
      const { from_date, to_date } = periodToDates(period, customRange);
      const params = {};
      if (from_date) params.from_date = from_date;
      if (to_date) params.to_date = to_date;
      const { data } = await api.get(`/masters/vendors/${id}/amount-ledger`, { params });
      setLedger(data);
    } catch (e) {
      // Don't fail the page if ledger lookup fails for some reason
      setLedger({ entries: [], balances: { amount_balance: 0, on_approval_balance: 0, outstanding_payable: 0 }, on_approval_packets: [], opening_balance: null, period: {} });
    }
  };

  useEffect(() => { loadVendor(); /* eslint-disable-next-line */ }, [id, effectiveCompanyId]);
  useEffect(() => { if (vendor) loadLedger(); /* eslint-disable-next-line */ }, [vendor, canViewAmountLedger, period, customRange.from, customRange.to]);

  // Wave 3.36 — Download per-vendor pending bills PDF for a chosen cutoff date.
  const downloadPendingBills = async () => {
    if (!pendingCutoff) {
      toast.error('Please pick a cutoff date.');
      return;
    }
    setPendingBusy(true);
    try {
      const params = { company_id: effectiveCompanyId, due_on_or_before: pendingCutoff };
      const res = await api.get(`/masters/vendors/${id}/pending-bills/pdf`, {
        params, responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (vendor?.name || 'vendor').replace(/\s+/g, '_').replace(/\//g, '-');
      a.download = `pending_bills_${safe}_${pendingCutoff}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Pending bills report downloaded');
      setPendingDlgOpen(false);
    } catch (e) {
      toast.error(extractError(e, 'Failed to generate report'));
    } finally {
      setPendingBusy(false);
    }
  };

  if (error) return <div className="text-rose-600 py-8" data-testid="vendor-error">{error}</div>;
  if (!vendor) return <div className="text-slate-500 py-8">Loading…</div>;

  const balances = ledger?.balances || { amount_balance: 0, on_approval_balance: 0, outstanding_payable: 0 };
  const onApprovalPackets = ledger?.on_approval_packets || [];

  return (
    <div className="space-y-5">
      {/* Header card with vendor info + balance stats (SA sees financial figures) */}
      <section id="vd-overview" className={sectionMat('teal')}>
      <Card className="p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <Building2 size={18} className="text-[var(--ink-navy)]" />
              <div className="text-xl font-medium text-[var(--ink-navy)]" data-testid="vendor-detail-name">{vendor.name}</div>
            </div>
            <div className="mt-1 text-xs text-slate-500">
              {vendor.contact_person && <span>{vendor.contact_person} · </span>}
              {vendor.phone && <span>{vendor.phone} · </span>}
              {vendor.email && <span>{vendor.email} · </span>}
              {vendor.gstin && <span>GSTIN {vendor.gstin}</span>}
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 min-w-[280px]">
            {canViewAmountLedger && (
              <>
                <Stat label="Outstanding Payable" value={formatINR(balances.outstanding_payable || 0)} tone="emerald" testid="vendor-outstanding-payable" />
                <Stat label="Amount Balance" value={formatINR(balances.amount_balance)} tone="slate" testid="vendor-amount-balance" />
              </>
            )}
            <Stat label="On Approval Value" value={formatINR(balances.on_approval_balance)} tone="amber" testid="vendor-on-approval-balance" />
          </div>
        </div>
        <div className="mt-3 flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => { loadVendor(); loadLedger(); }} data-testid="vendor-refresh-button"><RefreshCw size={12} className="mr-1.5" /> Refresh</Button>
          {isSuperAdmin && (
            <Button
              onClick={() => setPayOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              size="sm"
              data-testid="vendor-make-payment-button"
            >
              <Wallet size={14} className="mr-1.5" /> Make Payment
            </Button>
          )}
          {/* Phase 18 — Purchase Return (per material) */}
          <Button
            variant="outline"
            size="sm"
            className="border-amber-200 text-amber-800 hover:bg-amber-50"
            onClick={() => setPrDlg({ open: true, material: 'diamond' })}
            data-testid="vendor-purchase-return-diamond-button"
          >
            <ArrowDownToLine size={14} className="mr-1.5" /> PR · Diamonds
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-amber-200 text-amber-800 hover:bg-amber-50"
            onClick={() => setPrDlg({ open: true, material: 'color_stone' })}
            data-testid="vendor-purchase-return-color-stone-button"
          >
            <ArrowDownToLine size={14} className="mr-1.5" /> PR · Color Stones
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-amber-200 text-amber-800 hover:bg-amber-50"
            onClick={() => setPrDlg({ open: true, material: 'pearl' })}
            data-testid="vendor-purchase-return-pearl-button"
          >
            <ArrowDownToLine size={14} className="mr-1.5" /> PR · Pearls
          </Button>
          {/* Wave 3.36 — Per-vendor Pending Bills report */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setPendingCutoff(''); setPendingDlgOpen(true); }}
            data-testid="vendor-pending-bills-button"
          >
            Pending Bills
          </Button>
        </div>
      </Card>
      </section>

      {/* Wave 3.99k — Sticky section-nav chip bar. Conditionally
          includes On-Approval Packets only when the vendor actually
          has them.
          Wave 3.99m — Chips now surface small badge counts for
          list-style sections (On-Approval Packets and Amount Ledger
          entries when the viewer can see the ledger). */}
      <StickySectionNav
        sections={[
          { id: 'vd-overview', label: 'Overview', tint: 'teal' },
          { id: 'vd-make-purchase', label: 'Make Purchase', tint: 'amber' },
          ...(onApprovalPackets.length > 0
            ? [{
                id: 'vd-on-approval',
                label: 'On-Approval Packets',
                tint: 'violet',
                badge: onApprovalPackets.length,
              }]
            : []),
          {
            id: 'vd-amount-ledger',
            label: 'Amount Ledger',
            tint: 'indigo',
            badge: canViewAmountLedger
              ? ((ledger?.entries || []).length || undefined)
              : undefined,
          },
        ]}
        topOffset={56}
      />

      {/* Make Purchase — collapsible tile (default closed for clean UI) */}
      <section id="vd-make-purchase" className={sectionMat('amber')}>
      <MakePurchaseInline vendor={vendor} companyId={effectiveCompanyId} onPurchased={() => { loadLedger(); }} />
      </section>

      {/* On-approval packets */}
      {onApprovalPackets.length > 0 && (
        <section id="vd-on-approval" className={sectionMat('violet')}>
        <Card className="p-5">
          <div className="text-base font-medium text-[var(--ink-navy)] mb-2">On-Approval Packets</div>
          <div className="text-xs text-slate-500 mb-3">These packets are with you on approval. Use Convert to Stock from each packet&apos;s detail page to settle.</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2">Packet</th>
                  <th className="text-left px-3 py-2">Shape</th>
                  <th className="text-right px-3 py-2">Balance ct</th>
                  <th className="text-right px-3 py-2">Balance pcs</th>
                  <th className="text-right px-3 py-2">Value (₹)</th>
                  <th className="text-right px-3 py-2">Open</th>
                </tr>
              </thead>
              <tbody data-testid="vendor-on-approval-packets">
                {onApprovalPackets.map((p) => (
                  <tr key={p.id} className="border-t border-[var(--divider)]">
                    <td className="px-3 py-2"><span className="font-mono text-[12px] bg-[var(--diamond-bg)] text-[var(--diamond-icon)] px-2 py-0.5 rounded">{p.packet_no}</span></td>
                    <td className="px-3 py-2">{p.classification_shape}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatCarats(p.balance_carats)}</td>
                    <td className="px-3 py-2 text-right font-mono">{p.balance_pieces}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatINR(p.balance_value)}</td>
                    <td className="px-3 py-2 text-right">
                      <Link to={`/diamonds/packets/${p.id}`} className="text-xs text-[var(--ink-navy)] hover:underline" data-testid={`vendor-open-packet-${p.id}`}>Open →</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        </section>
      )}

      {/* Amount Ledger — gated by vendors.amount_ledger.view permission */}
      <section id="vd-amount-ledger" className={sectionMat('indigo')}>
      {canViewAmountLedger ? (
        <AmountLedgerCard
          ledger={ledger}
          period={period}
          setPeriod={setPeriod}
          customRange={customRange}
          setCustomRange={setCustomRange}
          entryTypeFilter={entryTypeFilter}
          setEntryTypeFilter={setEntryTypeFilter}
          onExportPdf={() => setPdfDlg({ open: true })}
        />
      ) : (
        <Card className="p-5 border-amber-200 bg-amber-50/40">
          <div className="flex items-start gap-3">
            <ShieldAlert size={18} className="text-amber-700 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-amber-900">Vendor Amount Ledger</div>
              <div className="text-xs text-amber-800 mt-0.5">
                Financial information (payables, payments and the running balance) is restricted.
                Ask a Super Admin to grant you the <span className="font-mono bg-amber-100 px-1 rounded">vendors.amount_ledger.view</span> permission.
              </div>
            </div>
          </div>
        </Card>
      )}
      </section>

      {/* Make Payment dialog (SA only) */}
      <MakePaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        vendor={vendor}
        onCreated={() => { loadLedger(); }}
      />

      {/* Phase 18 — Purchase Return (vendor mode) */}
      {prDlg.material && (
        <PurchaseReturnDialog
          open={prDlg.open}
          onOpenChange={(o) => setPrDlg({ ...prDlg, open: o })}
          mode="vendor"
          material={prDlg.material}
          companyId={effectiveCompanyId}
          vendorId={id}
          vendorName={vendor.name}
          onReturned={() => { setPrDlg({ open: false, material: prDlg.material }); loadLedger(); }}
        />
      )}
      {/* Phase 12 — PDF download dialog for vendor amount ledger */}
      {canViewAmountLedger && (
        <PdfDownloadDialog
          open={pdfDlg.open}
          onOpenChange={(o) => setPdfDlg({ ...pdfDlg, open: o })}
          title={`Vendor Amount Ledger \u2014 ${vendor?.name || ''}`}
          description="Choose whether to include the company logo and name on this ledger PDF."
          companyId={vendor?.company_id}
          endpoint={`/masters/vendors/${id}/amount-ledger/pdf`}
          filename={(() => {
            const { from_date, to_date } = periodToDates(period, customRange);
            const safe = (vendor?.name || 'vendor').replace(/\s+/g, '_').replace(/\//g, '-');
            const tag = (from_date || to_date)
              ? `_${(from_date || 'open').slice(0,10)}_to_${(to_date || 'open').slice(0,10)}`
              : '';
            return `AmountLedger_${safe}${tag}.pdf`;
          })()}
          extraParams={(() => {
            const { from_date, to_date } = periodToDates(period, customRange);
            const out = {};
            if (from_date) out.from_date = from_date;
            if (to_date) out.to_date = to_date;
            return out;
          })()}
          mode="download"
        />
      )}

      {/* Wave 3.36 — Per-vendor Pending Bills dialog. Cutoff is mandatory
          (user must pick) and triggers a direct PDF download on submit. */}
      {canViewAmountLedger && (
        <Dialog open={pendingDlgOpen} onOpenChange={setPendingDlgOpen}>
          <DialogContent className="max-w-md" data-testid="vendor-pending-bills-dialog">
            <DialogHeader>
              <DialogTitle>Pending Bills — {vendor?.name || 'Vendor'}</DialogTitle>
              <DialogDescription>
                Print every outstanding bill for this vendor whose due date falls on or before the chosen cutoff.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div>
                <Label className="text-xs text-slate-500">Due on or before *</Label>
                <DatePicker
                  value={pendingCutoff}
                  onChange={(e) => setPendingCutoff(e.target.value)}
                  data-testid="vendor-pending-bills-cutoff"
                />
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Bills with no due date are excluded.
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPendingDlgOpen(false)}>Cancel</Button>
              <Button
                onClick={downloadPendingBills}
                disabled={pendingBusy || !pendingCutoff}
                className="luxury-button"
                data-testid="vendor-pending-bills-download"
              >
                {pendingBusy ? 'Generating…' : 'Download PDF'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ============================================================================
// Phase 12 \u2014 reusable period filter + export toolbar for the amount ledger
// Wave 3.36 — extended with an Entry Type dropdown so the user can slice the
// ledger by activity flow (Purchases & Payments, Approval Movements, Payments
// only, or All entries).
// ============================================================================
function PeriodToolbar({ period, setPeriod, customRange, setCustomRange, entryTypeFilter, setEntryTypeFilter, onExportPdf }) {
  return (
    <div className="flex flex-wrap items-end gap-2 mb-3 pb-3 border-b border-[var(--divider)]">
      <div>
        <Label className="text-[11px] text-slate-500 flex items-center gap-1 mb-0.5">
          <Calendar size={11} /> Period
        </Label>
        <Select value={period} onValueChange={(v) => { setPeriod(v); if (v !== 'custom') setCustomRange({ from: '', to: '' }); }}>
          <SelectTrigger className="h-9 w-[200px]" data-testid="vendor-ledger-period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`vendor-ledger-period-option-${o.value}`}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {period === 'custom' && (
        <>
          <div>
            <Label className="text-[11px] text-slate-500 mb-0.5">From</Label>
            <DatePicker
              value={customRange.from}
              onChange={(e) => setCustomRange((p) => ({ ...p, from: e.target.value }))}
              className="h-9 w-[170px]"
              data-testid="vendor-ledger-custom-from"
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500 mb-0.5">To</Label>
            <DatePicker
              value={customRange.to}
              onChange={(e) => setCustomRange((p) => ({ ...p, to: e.target.value }))}
              className="h-9 w-[170px]"
              data-testid="vendor-ledger-custom-to"
            />
          </div>
        </>
      )}
      {/* Wave 3.36 — Entry Type filter */}
      <div>
        <Label className="text-[11px] text-slate-500 flex items-center gap-1 mb-0.5">
          <Filter size={11} /> Entry Type
        </Label>
        <Select value={entryTypeFilter} onValueChange={(v) => setEntryTypeFilter(v)}>
          <SelectTrigger className="h-9 w-[210px]" data-testid="vendor-ledger-entry-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(ENTRY_TYPE_GROUPS).map(([key, group]) => (
              <SelectItem
                key={key}
                value={key}
                data-testid={`vendor-ledger-entry-type-option-${key}`}
              >
                {group.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="ml-auto">
        <Button
          variant="outline"
          size="sm"
          className="h-9"
          onClick={onExportPdf}
          data-testid="vendor-ledger-export-pdf"
        >
          <Download size={14} className="mr-1.5" /> Export PDF
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Amount Ledger card with expandable rows
// ============================================================================
function AmountLedgerCard({ ledger, period, setPeriod, customRange, setCustomRange, entryTypeFilter, setEntryTypeFilter, onExportPdf }) {
  const allEntries = ledger?.entries || [];
  const openingBalance = ledger?.opening_balance || null;
  const [expanded, setExpanded] = useState({}); // id -> boolean

  // Wave 3.36 — filter entries by the selected entry-type group.
  const entries = useMemo(() => {
    const group = ENTRY_TYPE_GROUPS[entryTypeFilter] || ENTRY_TYPE_GROUPS.all;
    if (!group.types) return allEntries;
    const set = new Set(group.types);
    return allEntries.filter((e) => set.has(e.entry_type));
  }, [allEntries, entryTypeFilter]);

  // Opening balance is only meaningful when the user is looking at the
  // financial flow (or All). For the Approval-only view it would be
  // distracting, so suppress it there.
  const showOpening = openingBalance && entryTypeFilter !== 'approval';

  // Wave 3.36 — Column visibility per filter:
  //   purchase_payment / payment → hide "Bal On Approval"
  //   approval                   → hide "Bal Payable"
  //   all                        → show both
  const showBalPayable = entryTypeFilter !== 'approval';
  const showBalOnApproval = entryTypeFilter === 'all' || entryTypeFilter === 'approval';

  // Index payment entries by id so we can show full payment details when
  // expanded inside a purchase row. (Index over ALL entries so the filter
  // doesn't lose allocation context.)
  const paymentsById = useMemo(() => {
    const m = new Map();
    for (const e of allEntries) {
      if (e.entry_type === 'payment') m.set(e.id, e);
    }
    return m;
  }, [allEntries]);

  // Index allocations made TO each purchase entry (across all payments).
  const allocationsByPurchase = useMemo(() => {
    const m = new Map();
    for (const e of allEntries) {
      if (e.entry_type !== 'payment') continue;
      for (const a of e.allocations || []) {
        const pid = a?.purchase_entry_id;
        if (!pid) continue;
        const cur = m.get(pid) || [];
        cur.push({
          payment_id: e.id,
          payment_ref: e.reference,
          payment_note: e.note,
          payment_total: e.amount,
          payment_timestamp: e.timestamp,
          payment_by_email: e.created_by_email,
          alloc_amount: a.amount,
        });
        m.set(pid, cur);
      }
    }
    return m;
  }, [allEntries]);

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  if (entries.length === 0) {
    return (
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <ScrollText size={14} className="text-[var(--ink-navy)]" />
          <div className="text-base font-medium text-[var(--ink-navy)]">Amount Ledger</div>
          <Badge className="bg-emerald-50 text-emerald-700 text-[10px] ml-1">Super Admin only</Badge>
        </div>
        <PeriodToolbar
          period={period}
          setPeriod={setPeriod}
          customRange={customRange}
          setCustomRange={setCustomRange}
          entryTypeFilter={entryTypeFilter}
          setEntryTypeFilter={setEntryTypeFilter}
          onExportPdf={onExportPdf}
        />
        {showOpening && (
          <div className="px-3 py-2 mb-2 rounded-md border border-amber-200 bg-amber-50/60 flex items-center justify-between text-sm" data-testid="vendor-ledger-opening-balance">
            <div className="text-amber-900">
              <span className="font-medium">Opening balance brought forward</span>
              {openingBalance.as_of && <span className="text-amber-700 text-xs"> · as of {openingBalance.as_of.slice(0, 10)}</span>}
            </div>
            <div className="font-mono text-amber-900">
              Outstanding {formatINR(openingBalance.outstanding_payable || 0)}
              <span className="text-amber-700 text-xs ml-3">On Approval {formatINR(openingBalance.on_approval_balance || 0)}</span>
            </div>
          </div>
        )}
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed rounded-md">No entries in the selected period.</div>
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <ScrollText size={14} className="text-[var(--ink-navy)]" />
        <div className="text-base font-medium text-[var(--ink-navy)]">Amount Ledger</div>
        <Badge className="bg-emerald-50 text-emerald-700 text-[10px] ml-1">Super Admin only</Badge>
      </div>
      <PeriodToolbar
        period={period}
        setPeriod={setPeriod}
        customRange={customRange}
        setCustomRange={setCustomRange}
        entryTypeFilter={entryTypeFilter}
        setEntryTypeFilter={setEntryTypeFilter}
        onExportPdf={onExportPdf}
      />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="w-6"></th>
              <th className="text-left px-3 py-2">Date (IST)</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Packet / Ref</th>
              <th className="text-left px-3 py-2">Memo</th>
              {/* Wave 3.36 — Due date column for purchase/convert_to_stock rows */}
              <th className="text-left px-3 py-2">Due Date</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-right px-3 py-2">Paid / Allocated</th>
              <th className="text-right px-3 py-2">Outstanding</th>
              {showBalPayable && <th className="text-right px-3 py-2">Bal Payable</th>}
              {showBalOnApproval && <th className="text-right px-3 py-2">Bal On Approval</th>}
            </tr>
          </thead>
          <tbody data-testid="vendor-amount-ledger-table">
            {showOpening && (
              <tr className="bg-amber-50/70 border-t-2 border-amber-200" data-testid="vendor-ledger-opening-row">
                <td className="px-2"></td>
                <td className="px-3 py-2 text-xs text-amber-700">
                  {openingBalance.as_of ? openingBalance.as_of.slice(0, 10) : '\u2014'}
                </td>
                <td className="px-3 py-2">
                  <Badge className="text-[10px] bg-amber-100 text-amber-800">Opening</Badge>
                </td>
                <td colSpan={2} className="px-3 py-2 text-xs italic text-amber-800">
                  Opening balance brought forward
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td className="px-3 py-2 text-right font-mono text-amber-900">{formatINR(openingBalance.outstanding_payable || 0)}</td>
                {showBalPayable && <td className="px-3 py-2 text-right font-mono text-amber-900">{formatINR(openingBalance.amount_balance || 0)}</td>}
                {showBalOnApproval && <td className="px-3 py-2 text-right font-mono text-amber-900">{formatINR(openingBalance.on_approval_balance || 0)}</td>}
              </tr>
            )}
            {entries.map((e) => {
              const meta = ENTRY_META[e.entry_type] || { label: e.entry_type, tone: 'bg-slate-100' };
              const memoNo = e.return_memo_no || '';
              const isPurchase = e.entry_type === 'purchase' || e.entry_type === 'convert_to_stock';
              const isPayment = e.entry_type === 'payment';
              const allocations = isPurchase ? (allocationsByPurchase.get(e.id) || []) : [];
              const hasExpandable = isPurchase || isPayment;
              const outstanding = isPurchase
                ? Number(e.outstanding_amount ?? Math.max(0, (e.amount || 0) - (e.paid_amount || 0)))
                : 0;
              const paid = isPurchase ? Number(e.paid_amount || 0) : 0;
              const isOpen = !!expanded[e.id];
              // Wave 3.36 — Overdue detection: bill has a due_date that has passed AND still has outstanding > 0.
              const todayIso = todayIstIso();
              const isOverdue = isPurchase && e.due_date && e.due_date < todayIso && outstanding > 0.005;
              const outstandingTone = !isPurchase
                ? 'text-slate-400'
                : outstanding <= 0.001
                ? 'text-emerald-700'
                : paid > 0
                ? 'text-amber-700'
                : 'text-rose-700';
              return (
                <React.Fragment key={e.id}>
                  <tr
                    className={`border-t border-[var(--divider)] hover:bg-slate-50/60 ${isOverdue ? 'bg-rose-50 text-red-600 font-bold' : ''}`}
                    data-testid={`vendor-ledger-row-${e.id}`}
                  >
                    <td className="px-2">
                      {hasExpandable && (
                        <button
                          type="button"
                          onClick={() => toggle(e.id)}
                          className="p-1 text-slate-500 hover:text-[var(--ink-navy)]"
                          data-testid={`vendor-ledger-expand-${e.id}`}
                          title={isOpen ? 'Collapse' : 'Expand'}
                        >
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{formatDateTime(e.timestamp)}</td>
                    <td className="px-3 py-2">
                      <Badge className={`text-[10px] ${meta.tone}`}>{meta.label}</Badge>
                      {e.material_kind && (
                        <Badge variant="secondary" className="text-[10px] ml-1 capitalize">{e.material_kind === 'gold' ? 'Gold' : e.material_kind === 'diamond' ? 'Diamond' : e.material_kind}</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {e.packet_id ? (
                        <Link to={`/diamonds/packets/${e.packet_id}`} className="font-mono text-[11px] text-[var(--diamond-icon)] hover:underline">{e.packet_no}</Link>
                      ) : (
                        <span className="font-mono text-[11px] text-slate-400">{e.packet_no || '—'}</span>
                      )}
                      {e.reference && <span className="block text-[11px] text-slate-500 truncate" title={e.reference}>{e.reference}</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {memoNo ? (
                        <Link
                          to={`/return-memos/vendor/${encodeURIComponent(memoNo)}`}
                          className="inline-flex items-center gap-1 font-mono text-[11px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 hover:underline"
                          data-testid={`vendor-return-memo-${e.id}`}
                          title={`Open return memo ${memoNo}`}
                        >
                          <FileText size={10} />{memoNo}
                        </Link>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    {/* Wave 3.36 — Due date column. Red+bold for overdue,
                        muted '—' when none. */}
                    <td className="px-3 py-2 text-xs">
                      {isPurchase && e.due_date ? (
                        <span
                          className={`font-mono ${isOverdue ? 'text-red-700 font-bold' : 'text-slate-700'}`}
                          title={e.credit_days ? `Credit ${e.credit_days} day(s)` : undefined}
                          data-testid={`vendor-ledger-due-${e.id}`}
                        >
                          {(() => {
                            const [y, m, d] = e.due_date.split('-');
                            return `${d}-${m}-${y}`;
                          })()}
                          {isOverdue && <span className="ml-1 text-[10px] uppercase tracking-wide">overdue</span>}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{formatINR(e.amount)}</td>
                    <td className={`px-3 py-2 text-right font-mono ${isPurchase ? (paid > 0 ? 'text-emerald-700' : 'text-slate-400') : isPayment ? 'text-emerald-700' : 'text-slate-400'}`}>
                      {isPurchase ? formatINR(paid) : isPayment ? (
                        <span title={`Allocated to ${(e.allocations || []).length} purchases`}>
                          {formatINR((e.allocations || []).reduce((s, a) => s + (Number(a.amount) || 0), 0))}
                        </span>
                      ) : '—'}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono ${outstandingTone}`}>
                      {isPurchase ? formatINR(outstanding) : isPayment ? (
                        Number(e.unallocated_amount || 0) > 0
                          ? <span className="text-amber-700" title="Unallocated credit">{formatINR(e.unallocated_amount)} credit</span>
                          : <span className="text-slate-400">—</span>
                      ) : '—'}
                    </td>
                    {showBalPayable && (
                      <td className="px-3 py-2 text-right font-mono text-emerald-700">{formatINR(e.amount_balance)}</td>
                    )}
                    {showBalOnApproval && (
                      <td className="px-3 py-2 text-right font-mono text-amber-700">{formatINR(e.on_approval_balance)}</td>
                    )}
                  </tr>
                  {isOpen && isPurchase && (
                    <tr className="bg-slate-50/60 border-t border-[var(--divider)]" data-testid={`vendor-ledger-detail-${e.id}`}>
                      <td></td>
                      <td colSpan={8 + (showBalPayable ? 1 : 0) + (showBalOnApproval ? 1 : 0)} className="px-3 py-3">
                        <PurchaseDetails entry={e} allocations={allocations} />
                      </td>
                    </tr>
                  )}
                  {isOpen && isPayment && (
                    <tr className="bg-slate-50/60 border-t border-[var(--divider)]" data-testid={`vendor-ledger-detail-${e.id}`}>
                      <td></td>
                      <td colSpan={8 + (showBalPayable ? 1 : 0) + (showBalOnApproval ? 1 : 0)} className="px-3 py-3">
                        <PaymentDetails entry={e} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function PurchaseDetails({ entry, allocations }) {
  const li = entry.line_item || {};
  const kind = entry.material_kind || li.material_kind;
  // Pull whatever fields are present so the user always sees as much detail as we have.
  const carats = Number(li.carats || 0);
  const pieces = Number(li.pieces || 0);
  const ratePerCt = Number(li.rate_per_ct || 0);
  const weight = Number(li.weight_grams || 0);
  const purity = Number(li.purity_percent || 0);
  const pure999 = Number(li.pure_grams_999 || 0);
  const ratePerG = Number(li.rate_per_gram_999 || 0);
  const value = Number(li.value || entry.amount || 0);
  const hasAnyDetail = kind === 'diamond' || kind === 'gold' || carats || pieces || weight;
  return (
    <div className="space-y-3">
      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Line Items</div>
        {!hasAnyDetail ? (
          <div className="text-xs text-slate-500 italic py-2 px-2 border border-dashed rounded" data-testid="purchase-no-detail-warning">
            Detailed line item data isn&apos;t available for this legacy entry. The recorded value is {formatINR(value)}.
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white">
              <tr>
                <th className="text-left px-2 py-1 text-[10px] text-slate-500 uppercase">Material</th>
                {kind === 'diamond' && <>
                  <th className="text-left px-2 py-1 text-[10px] text-slate-500 uppercase">Packet</th>
                  <th className="text-right px-2 py-1 text-[10px] text-slate-500 uppercase">Carats</th>
                  <th className="text-right px-2 py-1 text-[10px] text-slate-500 uppercase">Pieces</th>
                  <th className="text-right px-2 py-1 text-[10px] text-slate-500 uppercase">Rate ₹/ct</th>
                </>}
                {kind === 'gold' && <>
                  <th className="text-right px-2 py-1 text-[10px] text-slate-500 uppercase">Weight (g)</th>
                  <th className="text-right px-2 py-1 text-[10px] text-slate-500 uppercase">Purity %</th>
                  <th className="text-right px-2 py-1 text-[10px] text-slate-500 uppercase">999 eqv (g)</th>
                  <th className="text-right px-2 py-1 text-[10px] text-slate-500 uppercase">Rate ₹/g</th>
                </>}
                <th className="text-right px-2 py-1 text-[10px] text-slate-500 uppercase">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-[var(--divider)]">
                <td className="px-2 py-1 capitalize">{kind || (entry.packet_no ? 'diamond' : '—')}</td>
                {kind === 'diamond' && <>
                  <td className="px-2 py-1 font-mono text-[11px] text-[var(--diamond-icon)]">{li.packet_no || entry.packet_no || '—'}</td>
                  <td className="px-2 py-1 text-right font-mono">{formatCarats(carats)}</td>
                  <td className="px-2 py-1 text-right font-mono">{pieces}</td>
                  <td className="px-2 py-1 text-right font-mono">{formatINR(ratePerCt)}</td>
                </>}
                {kind === 'gold' && <>
                  <td className="px-2 py-1 text-right font-mono">{formatGrams(weight)}</td>
                  <td className="px-2 py-1 text-right font-mono">{purity.toFixed(2)}%</td>
                  <td className="px-2 py-1 text-right font-mono">{formatGrams(pure999)}</td>
                  <td className="px-2 py-1 text-right font-mono">{formatINR(ratePerG)}</td>
                </>}
                <td className="px-2 py-1 text-right font-mono">{formatINR(value)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        )}
        {entry.note && <div className="text-[11px] text-slate-500 mt-1">Note: {entry.note}</div>}
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Payments Allocated ({allocations.length})</div>
        {allocations.length === 0 ? (
          <div className="text-xs text-slate-500 italic py-2 px-2 border border-dashed rounded">No payments allocated yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-white">
                <tr>
                  <th className="text-left px-2 py-1 text-[10px] text-slate-500 uppercase">Date</th>
                  <th className="text-left px-2 py-1 text-[10px] text-slate-500 uppercase">Reference</th>
                  <th className="text-right px-2 py-1 text-[10px] text-slate-500 uppercase">Allocated to this Purchase</th>
                  <th className="text-right px-2 py-1 text-[10px] text-slate-500 uppercase">Full Payment</th>
                  <th className="text-left px-2 py-1 text-[10px] text-slate-500 uppercase">Note</th>
                </tr>
              </thead>
              <tbody>
                {allocations.map((a, i) => (
                  <tr key={i} className="border-t border-[var(--divider)]">
                    <td className="px-2 py-1 text-slate-600">{formatDateTime(a.payment_timestamp)}</td>
                    <td className="px-2 py-1 text-slate-700">{a.payment_ref || '—'}</td>
                    <td className="px-2 py-1 text-right font-mono text-emerald-700">{formatINR(a.alloc_amount)}</td>
                    <td className="px-2 py-1 text-right font-mono text-slate-500">{formatINR(a.payment_total)}</td>
                    <td className="px-2 py-1 text-slate-500 truncate max-w-[200px]" title={a.payment_note || ''}>{a.payment_note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PaymentDetails({ entry }) {
  const allocations = entry.allocations || [];
  const unalloc = Number(entry.unallocated_amount || 0);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">Payment Allocations ({allocations.length})</div>
        {unalloc > 0 && (
          <Badge className="bg-amber-50 text-amber-800 text-[10px]">Unallocated credit: {formatINR(unalloc)}</Badge>
        )}
      </div>
      {allocations.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-2 px-2 border border-dashed rounded">
          This payment was recorded without allocations — the full amount sits as vendor credit.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white">
              <tr>
                <th className="text-left px-2 py-1 text-[10px] text-slate-500 uppercase">Purchase Date</th>
                <th className="text-left px-2 py-1 text-[10px] text-slate-500 uppercase">Material</th>
                <th className="text-left px-2 py-1 text-[10px] text-slate-500 uppercase">Packet</th>
                <th className="text-right px-2 py-1 text-[10px] text-slate-500 uppercase">Allocated</th>
              </tr>
            </thead>
            <tbody>
              {allocations.map((a, i) => (
                <tr key={i} className="border-t border-[var(--divider)]">
                  <td className="px-2 py-1 text-slate-600">{formatDateTime(a.purchase_timestamp)}</td>
                  <td className="px-2 py-1 capitalize">{a.material_kind || '—'}</td>
                  <td className="px-2 py-1">
                    {a.packet_id ? (
                      <Link to={`/diamonds/packets/${a.packet_id}`} className="font-mono text-[11px] text-[var(--diamond-icon)] hover:underline">{a.packet_no || '—'}</Link>
                    ) : (
                      <span className="font-mono text-[11px] text-slate-400">{a.packet_no || '—'}</span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right font-mono text-emerald-700">{formatINR(a.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {entry.note && <div className="text-[11px] text-slate-500 mt-1">Note: {entry.note}</div>}
    </div>
  );
}

function Stat({ label, value, tone = 'navy', testid }) {
  const toneClasses = {
    navy: 'bg-slate-50 text-[var(--ink-navy)] border-slate-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
  }[tone] || '';
  return (
    <div className={`px-4 py-3 rounded-md border ${toneClasses}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-base lg:text-lg font-mono font-medium whitespace-nowrap overflow-x-auto">{value}</div>
    </div>
  );
}
